import { socket } from "./socket";

export const checkAllDevices = async () => {
  const result = {
    microphone: { available: false, count: 0 },
    speaker: { available: false, count: 0 },
    camera: { available: false, count: 0 },
    allAvailable: false,
    canJoinAudioOnly: false,
    error: null,
    errorDetail: null,
  };

  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      result.error = "browser_unsupported";
      return result;
    }
    const devices = await navigator.mediaDevices.enumerateDevices();
    const mics = devices.filter((d) => d.kind === "audioinput");
    const speakers = devices.filter((d) => d.kind === "audiooutput");
    const cams = devices.filter((d) => d.kind === "videoinput");

    result.microphone = { available: mics.length > 0, count: mics.length };
    result.speaker = { available: speakers.length > 0, count: speakers.length };
    result.camera = { available: cams.length > 0, count: cams.length };
    result.allAvailable = mics.length > 0 && cams.length > 0;
    result.canJoinAudioOnly = mics.length > 0;
  } catch (error) {
    console.error("Erreur lors de la détection des périphériques :", error);
    if (window.isSecureContext === false) {
      result.error = "insecure_context";
    } else if (error.name === "NotAllowedError") {
      result.error = "permission_denied";
    } else {
      result.error = "unknown";
      result.errorDetail = error.message;
    }
  }
  return result;
};

export function classifyMediaError(error) {
  const map = {
    NotAllowedError: {
      titre: "Accès refusé",
      message:
        "L'accès au micro ou à la caméra a été refusé par votre navigateur.",
      solutions: [
        "Cliquez sur l'icône du cadenas à gauche de l'URL",
        "Autorisez l'accès au micro et à la caméra",
        "Actualisez la page",
      ],
    },
    NotFoundError: {
      titre: "Périphérique introuvable",
      message: "Le micro ou la caméra demandé n'a pas été trouvé.",
      solutions: [
        "Vérifiez que votre périphérique est bien branché",
        "Essayez un autre port USB",
        "Redémarrez votre navigateur",
      ],
    },
    NotReadableError: {
      titre: "Périphérique occupé",
      message: "Le micro ou la caméra est utilisé par une autre application.",
      solutions: [
        "Fermez les autres applications utilisant le micro/caméra (Zoom, Teams, Skype...)",
        "Redémarrez votre navigateur",
      ],
    },
    OverconstrainedError: {
      titre: "Configuration non supportée",
      message:
        "Les paramètres demandés ne sont pas supportés par votre périphérique.",
      solutions: [
        "Essayez avec des paramètres plus simples",
        "Mettez à jour les pilotes de votre périphérique",
      ],
    },
    AbortError: {
      titre: "Opération interrompue",
      message: "L'accès au périphérique a été interrompu.",
      solutions: [
        "Réessayez",
        "Vérifiez qu'aucun autre onglet n'utilise le périphérique",
      ],
    },
  };
  return (
    map[error.name] || {
      titre: "Erreur inattendue",
      message: error.message || "Une erreur inconnue est survenue.",
      solutions: [
        "Actualisez la page",
        "Essayez un autre navigateur (Chrome, Firefox, Edge)",
      ],
    }
  );
}

export const stopMediaStream = (stream, videoRef, setStream) => {
  if (videoRef.current && videoRef.current.srcObject) {
    videoRef.current.pause();
    videoRef.current.srcObject.getTracks().forEach((track) => {
      track.stop();
    });
    videoRef.current.load();
    videoRef.current.srcObject = null;
  }
  if (stream) {
    videoRef.current.pause();
    stream.getTracks().forEach((track) => {
      track.stop();
    });
    videoRef.current.load();
    videoRef.current.srcObject = null;
    setStream(null);
  }
};

export function attachStream(videoEl, stream, { muted = false } = {}) {
  if (!videoEl || !(stream instanceof MediaStream)) return;
  videoEl.srcObject = stream;
  videoEl.muted = !!muted; // local: true ; distants: true au départ
  videoEl.playsInline = true;
  videoEl.autoplay = true;
  videoEl.play().catch((err) => console.warn("autoplay bloqué:", err.message));
}

/**
 *
 * NOUVELLE IMPLEMENTATION
 *
 */

export const getMediasTracks = async (constraints) => {
  try {
    const localStream = await navigator.mediaDevices.getUserMedia(constraints);
    let audioTrack = localStream.getAudioTracks()[0];
    let videoTrack =
      constraints.video && constraints.audio
        ? localStream.getVideoTracks()[0]
        : null;
    return { audioTrack, videoTrack, error: null };
  } catch (error) {
    console.error("Erreur getUserMedia :", error);
    return {
      audioTrack: null,
      videoTrack: null,
      error: classifyMediaError(error),
    };
  }
};

export const upsertParticipant = (setParticipants, userId, updateData = {}) => {
  if (!userId) return;

  setParticipants((prev) => {
    const index = prev.findIndex((p) => p.userId === userId);
    const current = index !== -1 ? prev[index] : updateData;

    // Fusion générique
    const updated = {
      ...current,
      userId,
      displayName: updateData.displayName || current.displayName,
      userImage: updateData.userImage || current.userImage,
      isLocal: updateData.isLocal ?? current.isLocal ?? false,
    };

    if (index !== -1) {
      const copy = [...prev];
      copy[index] = updated;
      return copy;
    }

    return [...prev, updated];
  });
};

export const insertInLocalSorage = (values) => {
  const { res, device, meetingId, accessCode, constraints } = values;
  const { activeProducers, peers } = res;

  localStorage.setItem("producers", JSON.stringify(activeProducers));
  localStorage.setItem("peers", JSON.stringify(peers));
  localStorage.setItem("device", JSON.stringify(device));
  localStorage.setItem("meetingId", meetingId);
  localStorage.setItem("accessCode", accessCode);
  localStorage.setItem("constraints", JSON.stringify(constraints));
};

export async function toggleScreenShare(datas) {
  const {
    producerVideo,
    isScreenSharing,
    setIsScreenSharing,
    setScreenStream,
    screenStream,
    sendTransport,
    setProducerVideo,
    user,
    setLocalStream,
    localStream,
    participants,
    setParticipants,
  } = datas;

  // Si on est déjà en partage → on arrête et on revient à la caméra
  if (isScreenSharing) {
    stopScreenShare({
      screenStream,
      setScreenStream,
      producerVideo,
      setIsScreenSharing,
    });
    return;
  }

  // Sinon, on démarre le partage
  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
    });
    setScreenStream(stream);

    const screenTrack = stream.getVideoTracks()[0];

    // Quand l'utilisateur stoppe le partage depuis le navigateur
    screenTrack.onended = () => {
      stopScreenShare({
        screenStream: stream,
        setScreenStream,
        producerVideo: datas.producerVideo,
        setIsScreenSharing,
      });
    };

    if (producerVideo) {
      // Cas 1 : L'utilisateur a déjà un producer vidéo — remplacer la track
      await producerVideo.replaceTrack({ track: screenTrack });
    } else if (sendTransport) {
      // Cas 2 : Join audio-only — créer un nouveau producer vidéo
      const videoProducer = await sendTransport.produce({
        track: screenTrack,
        appData: {
          mediaTag: "video",
          userId: user.userId,
          userImage: user.userImage,
          displayName: user.prenom + " " + user.nom,
          enabledVideo: true,
          isScreenShare: true,
          msg: "Nouvelle vidéo en cours",
        },
      });
      setProducerVideo(videoProducer);

      if (localStream) {
        localStream.addTrack(screenTrack);
        setLocalStream(localStream);
      }

      setParticipants((prev) =>
        prev.map((p) =>
          p.userId === user.userId
            ? { ...p, videoOn: true, mediaTag: "video", stream: localStream }
            : p
        )
      );
    }

    // Informer les autres participants
    socket.emit("toggle_stream_video", {
      peer: participants.find((p) => p.userId === user.userId),
      videoOn: true,
      isScreenShare: true,
    });

    setIsScreenSharing(true);
  } catch (err) {
    console.error("Erreur lors du partage d'écran :", err);
  }
}

export async function stopScreenShare(datas) {
  const { screenStream, setScreenStream, producerVideo, setIsScreenSharing } =
    datas;
  if (screenStream) {
    screenStream.getTracks().forEach((t) => t.stop());
    setScreenStream(null);
  }

  if (producerVideo) {
    try {
      // Relancer la caméra
      const camStream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      const camTrack = camStream.getVideoTracks()[0];
      await producerVideo.replaceTrack({ track: camTrack });
    } catch (err) {
      // Si la caméra n'est pas disponible, mettre en pause
      console.warn("Impossible de revenir à la caméra:", err);
      await producerVideo.pause();
    }
  }

  setIsScreenSharing(false);
}

export const toggleVideo = async (datas, activeVideo) => {
  const {
    producerVideo,
    participants,
    setParticipants,
    displayVideo,
    setDisplayVideo,
    user,
  } = datas;
  const {
    sendTransport,
    setProducerVideo,
    localStream,
    setMyPeer,
    myPeer,
    setLocalStream,
    isMuted,
  } = activeVideo;
  const peer = participants.find((p) => p.userId === user.userId);
  try {
    if (producerVideo) {
      if (displayVideo) {
        peer.videoOn = false;
        peer.producerPaused = true;
        producerVideo.track.enabled = false;
        await producerVideo.pause();
      } else {
        peer.videoOn = true;
        peer.producerPaused = false;
        producerVideo.track.enabled = true;
        await producerVideo.resume();
      }
      socket.emit("toggle_stream_video", {
        peer,
        videoOn: !displayVideo,
      });
      setParticipants((prev) =>
        prev.map((p) =>
          p.userId === peer.userId
            ? {
                ...p,
                videoOn: peer.videoOn,
                producerPaused: peer.producerPaused,
              }
            : p
        )
      );
    } else {
      // 🎥 1️⃣ Obtenir un nouveau MediaStream vidéo
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const videoTrack = stream.getVideoTracks()[0];

      // 🧱 2️⃣ Créer un nouveau producer vidéo Mediasoup
      const videoProducer = await sendTransport.produce({
        track: videoTrack,
        appData: {
          msg: "Nouvelle vidéo en cours",
          mediaTag: "video",
          userId: user.userId,
          userImage: user.userImage,
          displayName:
            (user.prenom + " " + user.nom).length > 20
              ? (user.prenom + " " + user.nom).split(" ")[1] ||
                (user.prenom + " " + user.nom).split(" ")[0]
              : user.prenom + " " + user.nom,
          enabledVideo: true,
        },
      });
      setProducerVideo(videoProducer);
      localStream.addTrack(videoTrack);
      const me = {
        ...myPeer,
        mediaTag: "video",
        videoOn: true,
        audioOn: isMuted ? false : true,
        kind: "video",
        stream: localStream,
      };
      setMyPeer(me);
      setParticipants((prev) =>
        prev.map((p) => (p.userId === me.userId ? me : p))
      );
      setLocalStream(localStream);
    }
    setDisplayVideo(!displayVideo);
  } catch (err) {
    console.error("Erreur toggle vidéo:", err);
  }
};

export const toggleAudio = async function ({
  producerAudio,
  isMuted,
  setIsMuted,
  user,
  participants,
  setParticipants,
}) {
  const newState = !isMuted;

  const peer = participants.find((p) => p.userId === user.userId);
  try {
    if (newState) {
      peer.audioOn = false;
      peer.producerPaused = true;
      producerAudio.track.enabled = false;
      await producerAudio.pause();
    } else {
      peer.audioOn = true;
      peer.producerPaused = false;
      producerAudio.track.enabled = true;
      await producerAudio.resume();
    }

    socket.emit("toggle_stream_audio", {
      peer,
      audioOn: !newState,
    });

    // 🧭 Mettre à jour ton propre état local
    setIsMuted(newState);
    setParticipants((prev) =>
      prev.map((p) =>
        p.userId === peer.userId
          ? { ...p, audioOn: !newState, producerPaused: newState }
          : p
      )
    );
  } catch (err) {
    console.error("Erreur toggle audio:", err);
  }
};
